import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2,
  Plus,
  ArrowLeft,
  Shield,
  RefreshCw,
  Gift,
  Award,
  Users as UsersIcon,
  X,
  Trash2,
  AlertTriangle,
  Megaphone,
  Send,
  DollarSign,
} from "lucide-react";
import { http, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const TIER_BADGE = {
  free: "bg-ink-100 text-ink-700",
  starter: "bg-blue-50 text-blue-700",
  growth: "bg-purple-50 text-purple-700",
  scale: "bg-[#E8F5E9] text-[#2E7D32]",
  enterprise: "bg-ink-900 text-white",
};

const COMP_TIERS = ["starter", "growth", "scale", "enterprise"];
const CREDIT_AMOUNTS = [100, 500, 1000, 2500];
const EMAIL_CREDIT_AMOUNTS = [100, 500, 2000, 10000];
const MOBILE_CREDIT_AMOUNTS = [10, 50, 200, 500];
const SEAT_AMOUNTS = [1, 3, 5, 10];

export default function Admin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extendingId, setExtendingId] = useState(null);
  const [days, setDays] = useState(30);
  // Delete-account modal state (super-admin only)
  const [deleteTarget, setDeleteTarget] = useState(null); // {id, email}
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState("");
  const [deleting, setDeleting] = useState(false);
  // Broadcast state (product update)
  const [broadcastBusy, setBroadcastBusy] = useState(false);
  const [broadcastResult, setBroadcastResult] = useState(null);
  const [broadcastConfirmOpen, setBroadcastConfirmOpen] = useState(false);
  const [broadcastTypedConfirm, setBroadcastTypedConfirm] = useState("");
  // Activation-nudge broadcast state (dormant-user reactivation)
  const [nudgeBusy, setNudgeBusy] = useState(false);
  const [nudgeResult, setNudgeResult] = useState(null);
  const [nudgeConfirmOpen, setNudgeConfirmOpen] = useState(false);
  const [nudgeTypedConfirm, setNudgeTypedConfirm] = useState("");
  // User list filter: "all" | "verified" | "unverified"
  const [verifiedFilter, setVerifiedFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/users");
      setUsers(data.users || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (user && user.role !== "super_admin") {
    return (
      <div className="min-h-screen grid place-items-center text-center px-6">
        <div>
          <Shield className="w-10 h-10 text-ink-300 mx-auto mb-3" />
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-ink-900">
            Restricted area
          </h1>
          <p className="text-sm text-ink-500 mt-2 max-w-md">
            This page is for super-admins only. If you think this is a mistake, contact your administrator.
          </p>
          <Link to="/dashboard">
            <Button className="mt-6 rounded-sm" variant="outline">
              <ArrowLeft className="w-4 h-4" />
              Back to dashboard
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const onExtend = async (id, email) => {
    setExtendingId(id);
    try {
      const { data } = await http.post(`/admin/users/${id}/extend-trial`, { days });
      toast.success(`+${data.extended_days}d to ${email}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    } finally {
      setExtendingId(null);
    }
  };

  const onSetTier = async (id, tier, email) => {
    try {
      await http.post(`/admin/users/${id}/set-tier`, { tier });
      toast.success(`${email} → ${tier}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    }
  };

  const onCompTier = async (id, tier, days, email) => {
    try {
      await http.post(`/admin/users/${id}/comp-tier`, { tier, days });
      toast.success(`Granted ${tier} for ${days}d to ${email}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    }
  };

  const onGrantCredits = async (id, credits, email) => {
    try {
      await http.post(`/admin/users/${id}/grant-credits`, { credits, note: "" });
      toast.success(`+${credits.toLocaleString()} credits to ${email}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    }
  };

  const onGrantEmailCredits = async (id, credits, email) => {
    try {
      await http.post(`/admin/email-credits/gift`, { user_id: id, credits, note: "" });
      toast.success(`+${credits.toLocaleString()} email credits to ${email}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    }
  };

  const onGrantMobileCredits = async (id, credits, email) => {
    try {
      await http.post(`/admin/users/${id}/gift-mobile-credits`, { credits, reason: "admin_gift" });
      toast.success(`+${credits.toLocaleString()} mobile credits to ${email}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    }
  };

  const onGrantSeats = async (id, extra_seats, email) => {
    try {
      await http.post(`/admin/users/${id}/grant-seats`, { extra_seats });
      toast.success(`${email} → +${extra_seats} comp seats`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    }
  };

  const onRevokeComp = async (id, email) => {
    if (!window.confirm(`Revoke all comp grants for ${email} and revert to free?`)) return;
    try {
      await http.post(`/admin/users/${id}/revoke-comp`);
      toast.success(`Reverted ${email} to free`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    }
  };

  const onMarkVerified = async (id, email) => {
    if (!window.confirm(`Manually verify ${email}? They'll be able to use the platform immediately.`)) return;
    try {
      await http.post(`/admin/users/${id}/mark-verified`);
      toast.success(`${email} verified`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    }
  };

  // ---- Delete user account (super-admin only, password-confirmed) ----
  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeletePassword("");
    setDeleteConfirmEmail("");
  };

  // ---- Product update broadcast (super-admin only) ----
  const sendBroadcastTest = async () => {
    setBroadcastBusy(true);
    setBroadcastResult(null);
    try {
      const { data } = await http.post("/admin/announcements/product-update", {
        test_to: "annu.singh1106@gmail.com",
      });
      setBroadcastResult({ mode: "test", ...data });
      toast.success(data?.sent ? "Test email sent to annu.singh1106@gmail.com" : "Test send failed — check SMTP");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Test failed");
    } finally {
      setBroadcastBusy(false);
    }
  };

  const fireBroadcast = async () => {
    if (broadcastTypedConfirm.trim().toUpperCase() !== "BROADCAST") {
      toast.error('Type "BROADCAST" exactly to confirm');
      return;
    }
    setBroadcastBusy(true);
    setBroadcastResult(null);
    try {
      const { data } = await http.post("/admin/announcements/product-update", {
        confirm_broadcast: true,
      });
      setBroadcastResult({ mode: "broadcast", ...data });
      toast.success(`Broadcast complete · ${data?.sent_ok}/${data?.total_recipients} sent`);
      setBroadcastConfirmOpen(false);
      setBroadcastTypedConfirm("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Broadcast failed");
    } finally {
      setBroadcastBusy(false);
    }
  };

  // ---- Activation-nudge broadcast (dormant users, zero enrichments) ----
  const sendNudgeTest = async () => {
    setNudgeBusy(true);
    setNudgeResult(null);
    try {
      const { data } = await http.post("/admin/announcements/activation-nudge", {
        test_to: "annu.singh1106@gmail.com",
      });
      setNudgeResult({ mode: "test", ...data });
      toast.success(data?.sent ? "Test nudge sent to annu.singh1106@gmail.com" : "Test send failed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Test failed");
    } finally {
      setNudgeBusy(false);
    }
  };

  const fireNudgeBroadcast = async () => {
    if (nudgeTypedConfirm.trim().toUpperCase() !== "NUDGE") {
      toast.error('Type "NUDGE" exactly to confirm');
      return;
    }
    setNudgeBusy(true);
    setNudgeResult(null);
    try {
      const { data } = await http.post("/admin/announcements/activation-nudge", {
        confirm_broadcast: true,
      });
      setNudgeResult({ mode: "broadcast", ...data });
      toast.success(`Nudge sent · ${data?.sent_ok}/${data?.total_recipients} dormant users`);
      setNudgeConfirmOpen(false);
      setNudgeTypedConfirm("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Broadcast failed");
    } finally {
      setNudgeBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if (deleteConfirmEmail.trim().toLowerCase() !== deleteTarget.email.toLowerCase()) {
      toast.error("Email confirmation does not match");
      return;
    }
    setDeleting(true);
    try {
      const { data } = await http.delete(`/admin/users/${deleteTarget.id}`, {
        data: { password: deletePassword },
      });
      const total = Object.values(data?.deleted_counts || {}).reduce((a, b) => a + (b > 0 ? b : 0), 0);
      toast.success(`Deleted ${deleteTarget.email} · ${total} records purged`);
      closeDeleteModal();
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-2">
            Super-admin · Console
          </div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-none font-extrabold text-ink-900">
            User management.
          </h1>
          <p className="text-ink-500 text-sm mt-3 max-w-xl leading-relaxed">
            Extend trial access for any user, or move them between plans. Changes take effect immediately.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/admin/payments"
            data-testid="admin-payments-link"
            className="inline-flex items-center gap-1.5 bg-ink-900 hover:bg-ink-700 text-white text-[10px] uppercase tracking-widestx font-semibold px-3 h-10 rounded-sm"
          >
            <DollarSign className="w-3.5 h-3.5" />
            Payments
          </Link>
          <div className="flex items-center gap-2 bg-white border border-ink-100 rounded-sm px-3 h-10">
            <span className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">extend by</span>
            <input
              type="number"
              min={1}
              max={365}
              value={days}
              onChange={(e) => setDays(Math.max(1, Math.min(365, parseInt(e.target.value) || 30)))}
              data-testid="extend-days-input"
              className="w-12 text-center font-mono text-sm font-semibold text-ink-900 bg-transparent outline-none"
            />
            <span className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">days</span>
          </div>
          <Button
            data-testid="reload-users-button"
            variant="outline"
            size="sm"
            onClick={load}
            className="rounded-sm h-10 border-ink-200"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* ---- Broadcasts panel ---- */}
      <div
        data-testid="broadcasts-panel"
        className="bg-white border border-ink-100 rounded-sm p-5 mb-5"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-sm bg-brand/10 flex items-center justify-center shrink-0">
              <Megaphone className="w-5 h-5 text-brand" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <h2 className="font-display font-extrabold text-lg text-ink-900 tracking-tight">Product update broadcast</h2>
                <span className="text-[9px] uppercase font-mono tracking-widestx px-1.5 py-0.5 rounded-sm bg-amber-50 border border-amber-200 text-amber-700">
                  super-admin
                </span>
              </div>
              <p className="text-xs text-ink-600 max-w-xl leading-relaxed">
                Sends the latest &ldquo;New in LeadIQ Pro&rdquo; email (refundable email finder · real hiring signals ·
                funding intel · ESP detection) to every user in the database. Test it first, then confirm to broadcast.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              data-testid="broadcast-test-btn"
              size="sm"
              variant="outline"
              onClick={sendBroadcastTest}
              disabled={broadcastBusy}
              className="rounded-sm h-9 border-ink-200 text-[10px] uppercase tracking-widestx"
            >
              {broadcastBusy && broadcastResult?.mode !== "broadcast" ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Send className="w-3 h-3" />
              )}
              Send test to me
            </Button>
            <Button
              data-testid="broadcast-fire-btn"
              size="sm"
              onClick={() => {
                setBroadcastConfirmOpen(true);
                setBroadcastTypedConfirm("");
              }}
              disabled={broadcastBusy}
              className="rounded-sm h-9 bg-brand hover:bg-brand-hover text-white text-[10px] uppercase tracking-widestx"
            >
              <Megaphone className="w-3 h-3" />
              Broadcast to all
            </Button>
          </div>
        </div>

        {broadcastResult && (
          <div
            data-testid="broadcast-result"
            className="mt-4 bg-ink-50 border border-ink-100 rounded-sm p-3 font-mono text-[11px] text-ink-700 leading-relaxed"
          >
            {broadcastResult.mode === "test" ? (
              <>
                <strong className="text-ink-900">TEST →</strong> {broadcastResult.to} ·{" "}
                {broadcastResult.sent ? "✓ sent" : "✗ failed"} — {broadcastResult.message}
              </>
            ) : (
              <>
                <strong className="text-ink-900">BROADCAST →</strong>{" "}
                <span className="text-emerald-700 font-semibold">{broadcastResult.sent_ok}</span> ok ·{" "}
                <span className="text-red-600 font-semibold">{broadcastResult.failed_count}</span> failed ·{" "}
                {broadcastResult.total_recipients} total
                {broadcastResult.failed_count > 0 && broadcastResult.failed_sample?.length > 0 && (
                  <div className="mt-1.5 text-ink-500">
                    Failed sample: {broadcastResult.failed_sample.slice(0, 5).join(", ")}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* ---- Activation-nudge panel (dormant users) ---- */}
      <div
        data-testid="nudge-panel"
        className="bg-white border border-ink-100 rounded-sm p-5 mb-5"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-sm bg-emerald-50 flex items-center justify-center shrink-0">
              <Send className="w-5 h-5 text-emerald-700" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <h2 className="font-display font-extrabold text-lg text-ink-900 tracking-tight">
                  Wake-up nudge · dormant users
                </h2>
                <span className="text-[9px] uppercase font-mono tracking-widestx px-1.5 py-0.5 rounded-sm bg-amber-50 border border-amber-200 text-amber-700">
                  super-admin
                </span>
              </div>
              <p className="text-xs text-ink-600 max-w-xl leading-relaxed">
                Sends a warm, founder-personal reactivation email to every user who registered but
                <strong className="text-ink-800"> has never enriched a lead</strong>. Verified users only —
                unverified signups and already-active users are automatically skipped.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              data-testid="nudge-test-btn"
              size="sm"
              variant="outline"
              onClick={sendNudgeTest}
              disabled={nudgeBusy}
              className="rounded-sm h-9 border-ink-200 text-[10px] uppercase tracking-widestx"
            >
              {nudgeBusy && nudgeResult?.mode !== "broadcast" ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Send className="w-3 h-3" />
              )}
              Send test to me
            </Button>
            <Button
              data-testid="nudge-fire-btn"
              size="sm"
              onClick={() => {
                setNudgeConfirmOpen(true);
                setNudgeTypedConfirm("");
              }}
              disabled={nudgeBusy}
              className="rounded-sm h-9 bg-emerald-700 hover:bg-emerald-800 text-white text-[10px] uppercase tracking-widestx"
            >
              <Send className="w-3 h-3" />
              Nudge dormant users
            </Button>
          </div>
        </div>

        {nudgeResult && (
          <div
            data-testid="nudge-result"
            className="mt-4 bg-ink-50 border border-ink-100 rounded-sm p-3 font-mono text-[11px] text-ink-700 leading-relaxed"
          >
            {nudgeResult.mode === "test" ? (
              <>
                <strong className="text-ink-900">TEST →</strong> {nudgeResult.to} ·{" "}
                {nudgeResult.sent ? "✓ sent" : "✗ failed"} — {nudgeResult.message}
              </>
            ) : (
              <>
                <strong className="text-ink-900">NUDGE →</strong>{" "}
                <span className="text-emerald-700 font-semibold">{nudgeResult.sent_ok}</span> sent ·{" "}
                <span className="text-red-600 font-semibold">{nudgeResult.failed_count}</span> failed ·{" "}
                {nudgeResult.total_recipients} dormant recipients
                <div className="mt-1 text-ink-500">
                  Skipped: {nudgeResult.skipped_active ?? 0} already active ·{" "}
                  {nudgeResult.skipped_unverified ?? 0} unverified
                </div>
                {nudgeResult.failed_count > 0 && nudgeResult.failed_sample?.length > 0 && (
                  <div className="mt-1.5 text-ink-500">
                    Failed sample: {nudgeResult.failed_sample.slice(0, 5).join(", ")}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* ---- User-list filter ---- */}
      <div
        data-testid="user-filter-bar"
        className="flex items-center gap-2 mb-3 flex-wrap"
      >
        <span className="text-[10px] uppercase tracking-widestx font-mono text-ink-500 mr-1">
          Show
        </span>
        {[
          { key: "all", label: "All users" },
          { key: "verified", label: "Verified" },
          { key: "unverified", label: "Unverified only" },
        ].map((opt) => {
          const count =
            opt.key === "all"
              ? users.length
              : users.filter((u) =>
                  opt.key === "verified" ? u.email_verified !== false : u.email_verified === false,
                ).length;
          const active = verifiedFilter === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              data-testid={`filter-${opt.key}`}
              onClick={() => setVerifiedFilter(opt.key)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm text-[10px] uppercase tracking-widestx font-bold border transition-colors ${
                active
                  ? "bg-ink-900 text-white border-ink-900"
                  : "bg-white text-ink-700 border-ink-200 hover:border-ink-400"
              }`}
            >
              {opt.label}
              <span className={`font-mono ${active ? "text-white/70" : "text-ink-400"}`}>{count}</span>
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-ink-100 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-ink-50 border-b border-ink-100">
            <tr>
              {["User", "Role", "Tier", "Usage (mo)", "Find Mobile", "Trial", "Verified", "Created", "Actions"].map((h) => (
                <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="px-4 py-12 text-center text-ink-400">
                  <Loader2 className="w-4 h-4 animate-spin inline" />
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-12 text-center text-ink-400 text-sm">
                  No users yet.
                </td>
              </tr>
            ) : (
              users
                .filter((u) => {
                  if (verifiedFilter === "all") return true;
                  // Treat "email_verified === undefined" (legacy users) as VERIFIED
                  // — they're grandfathered per the backend policy.
                  const isVerified = u.email_verified !== false;
                  return verifiedFilter === "verified" ? isVerified : !isVerified;
                })
                .map((u) => (
                <tr
                  key={u.id}
                  data-testid={`user-row-${u.id}`}
                  className="border-b border-ink-100 last:border-0 hover:bg-ink-50/40"
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-ink-900">{u.name || u.email.split("@")[0]}</div>
                    <div className="text-[11px] font-mono text-ink-500">{u.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    {u.role === "super_admin" ? (
                      <span className="inline-flex items-center gap-1 bg-ink-900 text-white text-[10px] uppercase tracking-widestx font-bold px-2 py-1 rounded-sm">
                        <Shield className="w-2.5 h-2.5" /> super
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-widestx font-mono text-ink-500">
                        {u.role || "user"}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`inline-flex items-center text-[10px] uppercase tracking-widestx font-bold px-2 py-1 rounded-sm ${
                          TIER_BADGE[u.tier] || TIER_BADGE.free
                        }`}
                      >
                        {u.tier || "free"}
                      </span>
                      {u.is_comp && (
                        <span
                          data-testid={`comp-badge-${u.id}`}
                          className="inline-flex items-center gap-1 bg-[#FFF8E1] text-[#F57F17] text-[9px] uppercase tracking-widestx font-bold px-1.5 py-1 rounded-sm"
                          title={u.comp_until ? `Comp until ${u.comp_until.slice(0,10)}` : "Complimentary"}
                        >
                          <Gift className="w-2.5 h-2.5" />
                          comp
                        </span>
                      )}
                    </div>
                  </td>
                  {/* Usage (monthly enrichment credits) */}
                  <td className="px-4 py-3 text-xs" data-testid={`usage-cell-${u.id}`}>
                    {(() => {
                      const usage = u.usage || {};
                      const used = usage.month_used ?? 0;
                      const limit = usage.monthly_limit;         // null = unlimited
                      const pct = usage.pct_used;                 // null = unlimited
                      const lifetime = usage.lifetime_used ?? 0;
                      const isUnlimited = limit === null || limit === undefined;
                      // Color code the count
                      let numClass = "text-ink-700";
                      if (!isUnlimited && pct != null) {
                        if (pct >= 90) numClass = "text-[#C62828] font-semibold";
                        else if (pct >= 70) numClass = "text-[#F57F17] font-semibold";
                      }
                      return (
                        <div className="flex flex-col gap-0.5" title={`Lifetime enrichments: ${lifetime}`}>
                          <span className="font-mono text-[13px]">
                            <span className={numClass}>{used}</span>
                            {isUnlimited ? (
                              <span className="text-ink-400"> / ∞</span>
                            ) : (
                              <span className="text-ink-400"> / {limit}</span>
                            )}
                          </span>
                          {!isUnlimited && pct != null ? (
                            <span className="text-[10px] text-ink-400 font-mono">
                              {pct}% · {lifetime} lifetime
                            </span>
                          ) : (
                            <span className="text-[10px] text-ink-400 font-mono">
                              {lifetime} lifetime
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  {/* Find Mobile credits (lifetime) */}
                  <td className="px-4 py-3 text-xs" data-testid={`mobile-credits-cell-${u.id}`}>
                    {(() => {
                      const mc = u.mobile_credits || { purchased: 0, used: 0, balance: 0 };
                      const balance = mc.balance ?? 0;
                      const used = mc.used ?? 0;
                      const purchased = mc.purchased ?? 0;
                      let numClass = "text-ink-700";
                      if (balance === 0 && purchased > 0) numClass = "text-[#C62828] font-semibold";
                      else if (balance > 0 && balance <= 5) numClass = "text-[#F57F17] font-semibold";
                      else if (balance > 5) numClass = "text-[#2E7D32] font-semibold";
                      return (
                        <div
                          className="flex flex-col gap-0.5"
                          title={`Purchased: ${purchased} · Used: ${used} · Remaining: ${balance}`}
                        >
                          <span className="font-mono text-[13px]">
                            <span className={numClass} data-testid={`mobile-balance-${u.id}`}>{balance}</span>
                            <span className="text-ink-400"> left</span>
                          </span>
                          <span className="text-[10px] text-ink-400 font-mono">
                            <span data-testid={`mobile-used-${u.id}`}>{used}</span> used · {purchased} bought
                          </span>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {u.trial_active === false ? (
                      <span className="text-[#C62828] font-semibold">Expired</span>
                    ) : u.trial_days_left == null ? (
                      <span className="text-ink-400 font-mono">—</span>
                    ) : u.trial_days_left <= 7 ? (
                      <span className="text-[#F57F17] font-semibold font-mono">
                        {u.trial_days_left}d left
                      </span>
                    ) : (
                      <span className="text-ink-700 font-mono">{u.trial_days_left}d left</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {u.email_verified === false ? (
                      <button
                        type="button"
                        data-testid={`unverified-badge-${u.id}`}
                        onClick={() => onMarkVerified(u.id, u.email)}
                        title="Click to manually mark as verified (support flow)"
                        className="inline-flex items-center gap-1 bg-[#FFF3E0] hover:bg-[#FFE0B2] text-[#E65100] text-[9px] uppercase tracking-widestx font-bold px-1.5 py-1 rounded-sm border border-[#FFB74D] cursor-pointer transition-colors"
                      >
                        <AlertTriangle className="w-2.5 h-2.5" /> unverified · click to verify
                      </button>
                    ) : (
                      <span
                        data-testid={`verified-badge-${u.id}`}
                        className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 text-[9px] uppercase tracking-widestx font-bold px-1.5 py-1 rounded-sm border border-emerald-200"
                      >
                        <Shield className="w-2.5 h-2.5" /> verified
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[11px] font-mono text-ink-500">
                    {u.created_at ? u.created_at.slice(0, 10) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        data-testid={`extend-trial-${u.id}`}
                        size="sm"
                        variant="outline"
                        disabled={extendingId === u.id || u.role === "super_admin"}
                        onClick={() => onExtend(u.id, u.email)}
                        className="rounded-sm h-8 border-ink-200 text-[10px] uppercase tracking-widestx"
                      >
                        {extendingId === u.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Plus className="w-3 h-3" />
                        )}
                        +{days}d
                      </Button>
                      <select
                        data-testid={`set-tier-${u.id}`}
                        value={u.tier || "free"}
                        onChange={(e) => onSetTier(u.id, e.target.value, u.email)}
                        disabled={u.role === "super_admin"}
                        className="text-[10px] uppercase tracking-widestx font-semibold border border-ink-200 rounded-sm h-8 px-2 bg-white text-ink-700 disabled:opacity-50"
                      >
                        {["free", "starter", "growth", "scale", "enterprise"].map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>

                      {/* Comp tier */}
                      <select
                        data-testid={`comp-tier-${u.id}`}
                        defaultValue=""
                        onChange={(e) => {
                          const tier = e.target.value;
                          if (tier) {
                            onCompTier(u.id, tier, 30, u.email);
                            e.target.value = "";
                          }
                        }}
                        disabled={u.role === "super_admin"}
                        className="text-[10px] uppercase tracking-widestx font-semibold border border-[#F57F17]/30 rounded-sm h-8 px-2 bg-[#FFF8E1] text-[#F57F17] disabled:opacity-50"
                      >
                        <option value="">comp 30d…</option>
                        {COMP_TIERS.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>

                      {/* Gift credits */}
                      <select
                        data-testid={`grant-credits-${u.id}`}
                        defaultValue=""
                        onChange={(e) => {
                          const c = parseInt(e.target.value);
                          if (c) {
                            onGrantCredits(u.id, c, u.email);
                            e.target.value = "";
                          }
                        }}
                        className="text-[10px] uppercase tracking-widestx font-semibold border border-ink-200 rounded-sm h-8 px-2 bg-white text-ink-700"
                      >
                        <option value="">gift credits…</option>
                        {CREDIT_AMOUNTS.map((c) => (
                          <option key={c} value={c}>
                            +{c}
                          </option>
                        ))}
                      </select>

                      {/* Gift email credits */}
                      <select
                        data-testid={`grant-email-credits-${u.id}`}
                        defaultValue=""
                        onChange={(e) => {
                          const c = parseInt(e.target.value);
                          if (c) {
                            onGrantEmailCredits(u.id, c, u.email);
                            e.target.value = "";
                          }
                        }}
                        className="text-[10px] uppercase tracking-widestx font-semibold border border-[#FF6B35]/30 rounded-sm h-8 px-2 bg-[#FFF4ED] text-[#FF6B35]"
                      >
                        <option value="">gift email credits…</option>
                        {EMAIL_CREDIT_AMOUNTS.map((c) => (
                          <option key={c} value={c}>
                            +{c} ✉
                          </option>
                        ))}
                      </select>

                      {/* Gift mobile credits */}
                      <select
                        data-testid={`grant-mobile-credits-${u.id}`}
                        defaultValue=""
                        onChange={(e) => {
                          const c = parseInt(e.target.value);
                          if (c) {
                            onGrantMobileCredits(u.id, c, u.email);
                            e.target.value = "";
                          }
                        }}
                        className="text-[10px] uppercase tracking-widestx font-semibold border border-emerald-300 rounded-sm h-8 px-2 bg-emerald-50 text-emerald-800"
                      >
                        <option value="">gift mobile credits…</option>
                        {MOBILE_CREDIT_AMOUNTS.map((c) => (
                          <option key={c} value={c}>
                            +{c} 📱
                          </option>
                        ))}
                      </select>

                      {/* Gift seats */}
                      <select
                        data-testid={`grant-seats-${u.id}`}
                        defaultValue=""
                        onChange={(e) => {
                          const s = parseInt(e.target.value);
                          if (!Number.isNaN(s)) {
                            onGrantSeats(u.id, s, u.email);
                            e.target.value = "";
                          }
                        }}
                        className="text-[10px] uppercase tracking-widestx font-semibold border border-ink-200 rounded-sm h-8 px-2 bg-white text-ink-700"
                      >
                        <option value="">gift seats…</option>
                        {SEAT_AMOUNTS.map((s) => (
                          <option key={s} value={s}>
                            +{s}
                          </option>
                        ))}
                      </select>

                      {u.is_comp && (
                        <Button
                          data-testid={`revoke-comp-${u.id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => onRevokeComp(u.id, u.email)}
                          className="rounded-sm h-8 border-[#C62828]/30 text-[#C62828] text-[10px] uppercase tracking-widestx hover:bg-[#FFEBEE]"
                        >
                          <X className="w-3 h-3" />
                          revoke
                        </Button>
                      )}

                      {/* Delete account — super-admin only, requires password */}
                      {u.id !== user?.id && u.role !== "super_admin" && (
                        <Button
                          data-testid={`delete-user-${u.id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setDeleteTarget({ id: u.id, email: u.email });
                            setDeletePassword("");
                            setDeleteConfirmEmail("");
                          }}
                          className="rounded-sm h-8 border-[#C62828] bg-[#C62828] text-white text-[10px] uppercase tracking-widestx hover:bg-[#B71C1C] hover:text-white"
                          title={`Permanently delete ${u.email} and all their data`}
                        >
                          <Trash2 className="w-3 h-3" />
                          delete
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              )))}
          </tbody>
        </table>
      </div>

      {/* ---- Broadcast confirmation modal ---- */}
      {broadcastConfirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/60 backdrop-blur-sm px-4"
          onClick={() => !broadcastBusy && setBroadcastConfirmOpen(false)}
          data-testid="broadcast-modal-backdrop"
        >
          <div
            className="bg-white rounded-sm border border-ink-200 shadow-2xl max-w-md w-full p-6"
            onClick={(ev) => ev.stopPropagation()}
            data-testid="broadcast-modal"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-sm bg-brand/10 flex items-center justify-center shrink-0">
                <Megaphone className="w-5 h-5 text-brand" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widestx font-mono text-brand mb-1">
                  Send to every user
                </div>
                <h3 className="font-display text-xl font-extrabold text-ink-900 leading-tight">
                  Broadcast the product update?
                </h3>
              </div>
            </div>

            <p className="text-sm text-ink-600 leading-relaxed mb-4">
              This will email <strong>{users.length}</strong> user{users.length === 1 ? "" : "s"} in this environment with
              the &ldquo;New in LeadIQ Pro&rdquo; release notes. Sends are sequential to keep SMTP happy.
            </p>

            <div>
              <label className="text-[10px] uppercase tracking-widestx font-mono text-ink-500 mb-1.5 block">
                Type BROADCAST to confirm
              </label>
              <input
                data-testid="broadcast-typed-confirm"
                type="text"
                autoFocus
                value={broadcastTypedConfirm}
                onChange={(ev) => setBroadcastTypedConfirm(ev.target.value)}
                placeholder="BROADCAST"
                className="w-full border border-ink-300 rounded-sm h-9 px-3 text-sm font-mono focus:border-brand focus:outline-none"
                disabled={broadcastBusy}
                onKeyDown={(ev) => {
                  if (ev.key === "Enter") fireBroadcast();
                }}
              />
            </div>

            <div className="flex items-center justify-end gap-2 mt-6">
              <Button
                data-testid="broadcast-cancel-btn"
                variant="outline"
                onClick={() => setBroadcastConfirmOpen(false)}
                disabled={broadcastBusy}
                className="rounded-sm h-9 text-[10px] uppercase tracking-widestx"
              >
                Cancel
              </Button>
              <Button
                data-testid="broadcast-confirm-btn"
                onClick={fireBroadcast}
                disabled={broadcastBusy || broadcastTypedConfirm.trim().toUpperCase() !== "BROADCAST"}
                className="rounded-sm h-9 bg-brand hover:bg-brand-hover text-white text-[10px] uppercase tracking-widestx"
              >
                {broadcastBusy ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send className="w-3 h-3" />
                    Send to all users
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Nudge confirmation modal ---- */}
      {nudgeConfirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/60 backdrop-blur-sm px-4"
          onClick={() => !nudgeBusy && setNudgeConfirmOpen(false)}
          data-testid="nudge-modal-backdrop"
        >
          <div
            className="bg-white rounded-sm border border-ink-200 shadow-2xl max-w-md w-full p-6"
            onClick={(ev) => ev.stopPropagation()}
            data-testid="nudge-modal"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-sm bg-emerald-50 flex items-center justify-center shrink-0">
                <Send className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widestx font-mono text-emerald-700 mb-1">
                  Wake dormant users
                </div>
                <h3 className="font-display text-xl font-extrabold text-ink-900 leading-tight">
                  Send the activation nudge?
                </h3>
              </div>
            </div>

            <p className="text-sm text-ink-600 leading-relaxed mb-4">
              Emails will be sent only to verified users with{" "}
              <strong>zero enrichments</strong>. Already-active users and unverified signups are
              automatically excluded. Sends are sequential to keep the mail provider happy.
            </p>

            <div>
              <label className="text-[10px] uppercase tracking-widestx font-mono text-ink-500 mb-1.5 block">
                Type NUDGE to confirm
              </label>
              <input
                data-testid="nudge-typed-confirm"
                type="text"
                autoFocus
                value={nudgeTypedConfirm}
                onChange={(ev) => setNudgeTypedConfirm(ev.target.value)}
                placeholder="NUDGE"
                className="w-full border border-ink-300 rounded-sm h-9 px-3 text-sm font-mono focus:border-brand focus:outline-none"
                disabled={nudgeBusy}
                onKeyDown={(ev) => {
                  if (ev.key === "Enter") fireNudgeBroadcast();
                }}
              />
            </div>

            <div className="flex items-center justify-end gap-2 mt-6">
              <Button
                data-testid="nudge-cancel-btn"
                variant="outline"
                onClick={() => setNudgeConfirmOpen(false)}
                disabled={nudgeBusy}
                className="rounded-sm h-9 text-[10px] uppercase tracking-widestx"
              >
                Cancel
              </Button>
              <Button
                data-testid="nudge-confirm-btn"
                onClick={fireNudgeBroadcast}
                disabled={nudgeBusy || nudgeTypedConfirm.trim().toUpperCase() !== "NUDGE"}
                className="rounded-sm h-9 bg-emerald-700 hover:bg-emerald-800 text-white text-[10px] uppercase tracking-widestx"
              >
                {nudgeBusy ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send className="w-3 h-3" />
                    Send nudge
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}


      {/* ---- Delete confirmation modal ---- */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/60 backdrop-blur-sm px-4"
          onClick={closeDeleteModal}
          data-testid="delete-user-modal-backdrop"
        >
          <div
            className="bg-white rounded-sm border border-ink-200 shadow-2xl max-w-md w-full p-6"
            onClick={(ev) => ev.stopPropagation()}
            data-testid="delete-user-modal"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-sm bg-[#FFEBEE] flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-[#C62828]" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widestx font-mono text-[#C62828] mb-1">
                  Destructive · Irreversible
                </div>
                <h3 className="font-display text-xl font-extrabold text-ink-900 leading-tight">
                  Delete user account?
                </h3>
              </div>
            </div>

            <div className="bg-[#FFF5F5] border border-[#C62828]/20 rounded-sm p-3 mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-600 mb-1">Target user</div>
                <div className="font-mono text-sm text-ink-900 break-all">{deleteTarget.email}</div>
              </div>
            </div>

            <p className="text-sm text-ink-600 leading-relaxed mb-4">
              This permanently removes the user and <strong>all their data</strong> — leads,
              sequences, drafts, replies, SMTP/IMAP creds, billing profile.
              <br />
              <span className="text-ink-500 text-xs">
                Invoices &amp; credit purchases are preserved for audit/tax compliance.
              </span>
            </p>

            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-widestx font-mono text-ink-500 mb-1.5 block">
                  Type the user&apos;s email to confirm
                </label>
                <input
                  data-testid="delete-confirm-email"
                  type="text"
                  autoFocus
                  value={deleteConfirmEmail}
                  onChange={(ev) => setDeleteConfirmEmail(ev.target.value)}
                  placeholder={deleteTarget.email}
                  className="w-full border border-ink-300 rounded-sm h-9 px-3 text-sm font-mono focus:border-ink-900 focus:outline-none"
                  disabled={deleting}
                />
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-widestx font-mono text-ink-500 mb-1.5 block">
                  Admin deletion password
                </label>
                <input
                  data-testid="delete-admin-password"
                  type="password"
                  value={deletePassword}
                  onChange={(ev) => setDeletePassword(ev.target.value)}
                  placeholder="••••••••"
                  className="w-full border border-ink-300 rounded-sm h-9 px-3 text-sm font-mono focus:border-ink-900 focus:outline-none"
                  disabled={deleting}
                  onKeyDown={(ev) => {
                    if (ev.key === "Enter") confirmDelete();
                  }}
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 mt-6">
              <Button
                data-testid="delete-cancel-btn"
                variant="outline"
                onClick={closeDeleteModal}
                disabled={deleting}
                className="rounded-sm h-9 text-[10px] uppercase tracking-widestx"
              >
                Cancel
              </Button>
              <Button
                data-testid="delete-confirm-btn"
                onClick={confirmDelete}
                disabled={deleting || !deletePassword || !deleteConfirmEmail}
                className="rounded-sm h-9 bg-[#C62828] hover:bg-[#B71C1C] text-white text-[10px] uppercase tracking-widestx"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Deleting…
                  </>
                ) : (
                  <>
                    <Trash2 className="w-3 h-3" />
                    Permanently delete
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
